/**
 * LinkedIn Token Refresh Module (Story 10.1, Task 4)
 *
 * Refreshes LinkedIn access tokens using the refresh token.
 *
 * CRITICAL: LinkedIn refresh token TTL counts DOWN from the initial 365-day grant.
 * It does NOT reset on refresh. After refreshing on day 59, the refresh token has
 * 306 days left (365-59=306). When refresh token expires, user MUST re-authorize.
 *
 * Key differences from Meta:
 * - LinkedIn has a real refresh_token (Meta uses long-lived token exchange)
 * - No 24-hour minimum age guard (LinkedIn allows immediate refresh)
 * - refresh_token_expires_in decrements, never resets
 */

import { refreshLinkedInToken } from "./oauth";

// ============================================================================
// Constants
// ============================================================================

/** 7 days in seconds — flag as expiring_soon when refresh token has < 7 days left */
const REFRESH_EXPIRY_THRESHOLD_SECONDS = 604800;

// ============================================================================
// Types
// ============================================================================

export interface LinkedInRefreshResult {
  status: "success" | "expiring_soon" | "error";
  accessToken?: string;
  expiresIn?: number;
  refreshToken?: string;
  refreshTokenExpiresIn?: number;
  error?: string;
}

// ============================================================================
// Token Refresh (Task 4.1)
// ============================================================================

/**
 * Refreshes a LinkedIn access token.
 *
 * Uses the refresh token to obtain a new access token from LinkedIn's token endpoint.
 *
 * CRITICAL: refresh_token_expires_in in the response is the REMAINING time,
 * counting down from the original 365-day grant. Do NOT reset expiry.
 *
 * When refreshTokenExpiresIn < 604800 (7 days), returns status: "expiring_soon"
 * to signal that the user should re-authorize soon.
 *
 * @param refreshToken - The current refresh token
 * @param clientId - LinkedIn app client ID
 * @param clientSecret - LinkedIn app client secret
 * @returns Refresh result with new access token and updated expiry values
 */
export async function refreshLinkedInAccessToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string
): Promise<LinkedInRefreshResult> {
  try {
    const response = await refreshLinkedInToken(refreshToken, clientId, clientSecret);

    const status =
      response.refresh_token_expires_in < REFRESH_EXPIRY_THRESHOLD_SECONDS
        ? "expiring_soon"
        : "success";

    return {
      status,
      accessToken: response.access_token,
      expiresIn: response.expires_in,
      refreshToken: response.refresh_token,
      refreshTokenExpiresIn: response.refresh_token_expires_in,
    };
  } catch (err) {
    return {
      status: "error",
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
